import React from 'react';
import { ErrorCode, FileRejection } from 'react-dropzone';
import { ErrorCodeSummaryText, MaxLimits, PhotoExtensions, VideoExtensions } from '../constants/media';

export type UniqueFileRejectionErrorType = {
  code: string;
  summary: string;
  message: string;
  files: string[];
};

export const getAllowedFilesExtensions = () => {
  const videosExtensions = Object.keys(VideoExtensions);
  const imagesExtensions = Object.keys(PhotoExtensions);
  const allowedExtensions = Array.from(new Set([...videosExtensions, ...imagesExtensions]));
  return `${allowedExtensions.join(', ').toLowerCase()}.`;
};

// Simple mapping that allows for the mixing of ErrorCodes
export const mapErrorCodeToSummaryText = (errorCode: string) => {
  switch (errorCode) {
    case ErrorCode.FileInvalidType:
      return ErrorCodeSummaryText['file-invalid-type'];
    case ErrorCode.FileTooLarge:
      return ErrorCodeSummaryText['file-too-large'];
    case ErrorCode.FileTooSmall:
      return ErrorCodeSummaryText['file-too-small'];
    case ErrorCode.TooManyFiles:
      return ErrorCodeSummaryText['too-many-files'];
    default:
      return 'Error trying to upload';
  }
};

const getErrorCodeDetailContent = ({
  errorCode,
  filesExtensions,
  maxFilesAllowed,
  countInvalids,
  isSingular
}: {
  errorCode: ErrorCode;
  filesExtensions?: string;
  maxFilesAllowed?: number;
  countInvalids?: number;
  isSingular: boolean;
}) => {
  if (isSingular) {
    const detailContent = {
      'file-invalid-type': (
        <>
          {' '}
          <p className={'small-text'}>
            The file you&apos;re trying to upload doesn&apos;t have a valid extension. Valid file extensions are:
          </p>{' '}
          <p className={'small-text'}>{filesExtensions}</p>{' '}
        </>
      ),
      'file-too-large':
        'The selected file is too large. Please choose a file that is smaller than the maximum file size.',
      'file-too-small':
        'The selected file is too small. Please choose a file that is larger than the minimum file size.',
      'too-many-files': `Only ${maxFilesAllowed} file can be uploaded at one time.`
    };
    return detailContent[errorCode];
  } else {
    const detailContent = {
      'file-invalid-type': (
        <>
          <p className={'small-text'}>
            {countInvalids} of the files you&apos;re trying to upload don&apos;t have a valid extension. Valid file type
            extensions are:
          </p>{' '}
          <p className={'small-text'}>{filesExtensions}</p>
        </>
      ),
      'file-too-large': `${countInvalids} of the selected files are too large. Please choose files that are smaller than the maximum file size.`,
      'file-too-small': `${countInvalids} of the selected files are too small. Please choose files that are larger than the minimum file size.`,
      'too-many-files': `Only ${countInvalids} files can be uploaded at one time.`
    };
    return detailContent[errorCode];
  }
};

export const getUniqueFileRejectionErrors = (fileRejections: FileRejection[]) =>
  fileRejections.reduce<UniqueFileRejectionErrorType[]>((errorType, { file, errors }) => {
    errors.forEach(error => {
      const existing = errorType.find(item => item.code === error.code && item.message === error.message);

      if (existing) {
        existing.files.push(file.name);
      } else {
        errorType.push({
          code: error.code,
          summary: mapErrorCodeToSummaryText(error.code),
          message: error.message,
          files: [file.name]
        });
      }
    });

    return errorType;
  }, []);

const isValidErrorCode = (code: string): code is ErrorCode => {
  return Object.values(ErrorCode).includes(code as ErrorCode);
};

export const getDetailText = (errorCode: string, count: number): string | React.ReactNode => {
  if (!isValidErrorCode(errorCode)) return '';

  const isSingular = count === 1;
  const filesExtensions = getAllowedFilesExtensions();
  const detailText = getErrorCodeDetailContent({
    isSingular,
    errorCode,
    countInvalids: count,
    filesExtensions,
    maxFilesAllowed: MaxLimits.UPLOAD
  });

  if (errorCode === ErrorCode.FileInvalidType) {
    return <>{detailText}</>;
  }
  return detailText;
};
